import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { deviations as seedDeviations, capas as seedCapas, changeControls as seedChangeControls } from '../data/mockData';
import { useApp } from './AppContext';
import { useFilters } from './FilterContext';

const QualityEventsContext = createContext(null);

// Permission map
const getPermissions = (roleId, isReadOnly) => {
  if (isReadOnly || roleId === 'EXT_AUDITOR') {
    return {
      deviation: { create: false, startInvestigation: false, close: false, approve: false },
      capa: { create: false, verifyEffectiveness: false, close: false },
      cc: { create: false, approve: false, close: false }
    };
  }

  const permissions = {
    deviation: { create: true, startInvestigation: true, close: false, approve: false },
    capa: { create: true, verifyEffectiveness: false, close: false },
    cc: { create: true, approve: false, close: false }
  };

  // QA Head can approve/close
  if (roleId === 'QA_HEAD') {
    permissions.deviation.approve = true;
    permissions.deviation.close = true;
    permissions.capa.verifyEffectiveness = true;
    permissions.capa.close = true;
    permissions.cc.approve = true;
    permissions.cc.close = true;
  }

  // QA Officer can start investigations and create CAPAs
  if (roleId === 'QA_OFFICER') {
    permissions.deviation.startInvestigation = true;
    permissions.capa.create = true;
  }

  // Production Supervisor can create deviations in their area
  if (roleId === 'PROD_SUP') {
    permissions.deviation.create = true;
  }

  // Engineering can create change controls
  if (roleId === 'ENGINEERING') {
    permissions.cc.create = true;
  }

  return permissions;
};

// Load from localStorage or use seed data
const loadFromStorage = (key, seedData) => {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Only use stored data if it's a valid array with items
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
      // If stored is empty array, clear it and use seed data
      if (Array.isArray(parsed) && parsed.length === 0) {
        console.log(`localStorage ${key} is empty array, clearing and using seed data`);
        localStorage.removeItem(key); // Clear empty array
        return seedData || [];
      }
    }
  } catch (e) {
    console.warn(`Failed to load ${key} from localStorage:`, e);
  }
  // Always return seed data if localStorage is empty or invalid
  return seedData || [];
};

// Save to localStorage
const saveToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.warn(`Failed to save ${key} to localStorage:`, e);
  }
};

export const QualityEventsProvider = ({ children }) => {
  const { currentRole, isReadOnly } = useApp();
  const { plant, area } = useFilters();

  // Load initial data - ALWAYS start with seed data, then check localStorage
  const [deviations, setDeviations] = useState(() => {
    // First, check if localStorage has valid data
    try {
      const stored = localStorage.getItem('smcp_deviations');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Only use stored if it has items
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
        // If empty array, clear it
        if (Array.isArray(parsed) && parsed.length === 0) {
          localStorage.removeItem('smcp_deviations');
        }
      }
    } catch (e) {
      console.warn('Failed to load deviations:', e);
      localStorage.removeItem('smcp_deviations');
    }
    // Always return seed data as fallback
    return seedDeviations;
  });
  
  const [capas, setCapas] = useState(() => {
    try {
      const stored = localStorage.getItem('smcp_capas');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
        if (Array.isArray(parsed) && parsed.length === 0) {
          localStorage.removeItem('smcp_capas');
        }
      }
    } catch (e) {
      console.warn('Failed to load CAPAs:', e);
      localStorage.removeItem('smcp_capas');
    }
    return seedCapas;
  });
  
  const [changeControls, setChangeControls] = useState(() => {
    try {
      const stored = localStorage.getItem('smcp_change_controls');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
        if (Array.isArray(parsed) && parsed.length === 0) {
          localStorage.removeItem('smcp_change_controls');
        }
      }
    } catch (e) {
      console.warn('Failed to load change controls:', e);
      localStorage.removeItem('smcp_change_controls');
    }
    return seedChangeControls;
  });

  // On mount, double-check and ensure seed data is loaded if empty
  useEffect(() => {
    if (deviations.length === 0) {
      console.log('⚠️ Deviations array is empty! Loading seed data...');
      setDeviations(seedDeviations);
      saveToStorage('smcp_deviations', seedDeviations);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (capas.length === 0) {
      console.log('⚠️ CAPAs array is empty! Loading seed data...');
      setCapas(seedCapas);
      saveToStorage('smcp_capas', seedCapas);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (changeControls.length === 0) {
      console.log('⚠️ Change controls array is empty! Loading seed data...');
      setChangeControls(seedChangeControls);
      saveToStorage('smcp_change_controls', seedChangeControls);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist to localStorage whenever data changes
  useEffect(() => {
    saveToStorage('smcp_deviations', deviations);
  }, [deviations]);

  useEffect(() => {
    saveToStorage('smcp_capas', capas);
  }, [capas]);

  useEffect(() => {
    saveToStorage('smcp_change_controls', changeControls);
  }, [changeControls]);

  // Permissions
  const permissions = useMemo(() => getPermissions(currentRole?.id, isReadOnly), [currentRole?.id, isReadOnly]);

  // Helper: Generate next ID
  const generateDeviationId = useCallback((areaCode) => {
    const year = new Date().getFullYear();
    const prefix = areaCode ? areaCode.replace('AREA-', '') : 'GEN';
    const existing = deviations.filter(d => d.id.startsWith(`DEV-${prefix}-${year}`));
    const nextSeq = String(existing.length + 1).padStart(3, '0');
    return `DEV-${prefix}-${year}-${nextSeq}`;
  }, [deviations]);

  const generateCAPAId = useCallback(() => {
    const year = new Date().getFullYear();
    const existing = capas.filter(c => c.id.startsWith(`CAPA-${year}`));
    const nextSeq = String(existing.length + 1).padStart(3, '0');
    return `CAPA-${year}-${nextSeq}`;
  }, [capas]);

  const generateCCId = useCallback((areaCode) => {
    const year = new Date().getFullYear();
    const prefix = areaCode ? areaCode.replace('AREA-', '') : 'GEN';
    const existing = changeControls.filter(cc => cc.id.startsWith(`CC-${prefix}-${year}`));
    const nextSeq = String(existing.length + 1).padStart(3, '0');
    return `CC-${prefix}-${year}-${nextSeq}`;
  }, [changeControls]);

  // ========== DEVIATIONS ==========
  const createDeviation = useCallback((payload) => {
    const areaCode = payload.area || area;
    const newDeviation = {
      id: generateDeviationId(areaCode),
      title: payload.title || payload.shortDescription,
      description: payload.detailedDescription || payload.title,
      area: areaCode,
      plant: payload.plant || plant || 'PLANT-A', // Default to PLANT-A
      severity: payload.severity,
      status: 'Open',
      category: payload.category,
      reportedBy: payload.reportedBy || currentRole?.name || 'System',
      reportedAt: payload.eventDateTime || new Date().toISOString(),
      batch: payload.batch || null,
      equipment: payload.equipment || null,
      room: payload.room || null,
      capaId: null,
      rootCause: null,
      investigation: null,
      attachments: payload.attachments || [],
      immediateAction: payload.immediateAction || '',
      linkedReqs: payload.linkedReqs || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: currentRole?.name || 'System',
      currentOwnerRole: currentRole?.id || 'QA_OFFICER'
    };
    setDeviations(prev => [...prev, newDeviation]);
    return newDeviation;
  }, [generateDeviationId, area, plant, currentRole]);

  const updateDeviation = useCallback((id, patch) => {
    setDeviations(prev => prev.map(d => 
      d.id === id ? { ...d, ...patch, updatedAt: new Date().toISOString() } : d
    ));
  }, []);

  const addDeviationAttachment = useCallback((id, attachment) => {
    setDeviations(prev => prev.map(d => 
      d.id === id 
        ? { ...d, attachments: [...(d.attachments || []), { ...attachment, addedAt: new Date().toISOString() }] }
        : d
    ));
  }, []);

  const startInvestigation = useCallback((deviationId) => {
    const deviation = deviations.find(d => d.id === deviationId);
    if (!deviation || deviation.status !== 'Open') return;

    const investigation = {
      owner: currentRole?.name || 'QA Officer',
      ownerRole: currentRole?.id || 'QA_OFFICER',
      startedAt: new Date().toISOString(),
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      problemStatement: '',
      whatHappened: '',
      impact: '',
      containment: '',
      rootCause: '',
      proposedFix: '',
      capaRequired: null,
      attachments: [],
      notes: ''
    };

    updateDeviation(deviationId, {
      status: 'Under Investigation',
      investigation
    });

    return investigation;
  }, [deviations, currentRole, updateDeviation]);

  const saveInvestigation = useCallback((deviationId, patch) => {
    const deviation = deviations.find(d => d.id === deviationId);
    if (!deviation || !deviation.investigation) return;

    updateDeviation(deviationId, {
      investigation: { ...deviation.investigation, ...patch }
    });
  }, [deviations, updateDeviation]);

  const assignCAPAFromDeviation = useCallback((deviationId, capaDraft) => {
    const deviation = deviations.find(d => d.id === deviationId);
    if (!deviation) return null;

    // Create CAPA directly (we'll define createCAPA before this)
    const year = new Date().getFullYear();
    const existing = capas.filter(c => c.id.startsWith(`CAPA-${year}`));
    const nextSeq = String(existing.length + 1).padStart(3, '0');
    const newCAPAId = `CAPA-${year}-${nextSeq}`;
    
    const newCAPA = {
      id: newCAPAId,
      title: capaDraft.title || `CAPA for ${deviation.title}`,
      deviationId,
      status: 'Open',
      priority: capaDraft.priority || 'Medium',
      owner: currentRole?.name || 'QA Officer',
      ownerRole: currentRole?.id || 'QA_OFFICER',
      dueDate: capaDraft.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      effectivenessCheck: capaDraft.effectivenessCheck || '30 days',
      progress: 0,
      correctiveActions: capaDraft.correctiveActions || [],
      preventiveActions: capaDraft.preventiveActions || [],
      tasks: capaDraft.tasks || [],
      effectivenessResult: null,
      effectivenessVerifiedBy: null,
      effectivenessVerifiedAt: null,
      linkedReqs: capaDraft.linkedReqs || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: currentRole?.name || 'System'
    };
    
    setCapas(prev => [...prev, newCAPA]);

    updateDeviation(deviationId, {
      status: 'CAPA Assigned',
      capaId: newCAPA.id
    });

    return newCAPA;
  }, [deviations, capas, currentRole, updateDeviation]);

  const closeDeviation = useCallback((deviationId, closureNotes) => {
    const deviation = deviations.find(d => d.id === deviationId);
    if (!deviation) return;

    // Check if CAPA is required and completed
    if (deviation.investigation?.capaRequired === true) {
      const linkedCAPA = capas.find(c => c.id === deviation.capaId);
      if (!linkedCAPA || linkedCAPA.status !== 'Closed') {
        throw new Error('Cannot close deviation: Linked CAPA must be closed first');
      }
    } else if (deviation.investigation?.capaRequired === false) {
      // No CAPA required - can close if approved (mock approval for QA Head)
      if (currentRole?.id !== 'QA_HEAD') {
        throw new Error('Cannot close deviation: QA Head approval required for "No CAPA" closure');
      }
    }

    updateDeviation(deviationId, {
      status: 'Closed',
      closedAt: new Date().toISOString(),
      closureNotes
    });
  }, [deviations, capas, currentRole, updateDeviation]);

  const getDeviationById = useCallback((id) => {
    return deviations.find(d => d.id === id);
  }, [deviations]);

  // ========== CAPA ==========
  const createCAPA = useCallback((payload) => {
    const newCAPA = {
      id: generateCAPAId(),
      title: payload.title,
      deviationId: payload.deviationId || null,
      status: payload.status || 'Open',
      priority: payload.priority || 'Medium',
      owner: payload.owner || currentRole?.name || 'QA Officer',
      ownerRole: payload.ownerRole || currentRole?.id || 'QA_OFFICER',
      dueDate: payload.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      effectivenessCheck: payload.effectivenessCheck || '30 days',
      progress: 0,
      correctiveActions: payload.correctiveActions || [],
      preventiveActions: payload.preventiveActions || [],
      tasks: payload.tasks || [],
      effectivenessResult: null,
      effectivenessVerifiedBy: null,
      effectivenessVerifiedAt: null,
      linkedReqs: payload.linkedReqs || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: currentRole?.name || 'System'
    };
    setCapas(prev => [...prev, newCAPA]);
    return newCAPA;
  }, [generateCAPAId, currentRole]);

  const updateCAPA = useCallback((id, patch) => {
    setCapas(prev => prev.map(c => 
      c.id === id ? { ...c, ...patch, updatedAt: new Date().toISOString() } : c
    ));
  }, []);

  const addCAPATask = useCallback((id, task) => {
    const capa = capas.find(c => c.id === id);
    if (!capa) return;

    const newTask = {
      id: `task-${Date.now()}`,
      ...task,
      status: 'Open',
      createdAt: new Date().toISOString()
    };

    updateCAPA(id, {
      tasks: [...(capa.tasks || []), newTask]
    });
  }, [capas, updateCAPA]);

  const completeCAPATask = useCallback((id, taskId) => {
    const capa = capas.find(c => c.id === id);
    if (!capa) return;

    const updatedTasks = (capa.tasks || []).map(t => 
      t.id === taskId ? { ...t, status: 'Completed', completedAt: new Date().toISOString() } : t
    );

    const allCompleted = updatedTasks.every(t => t.status === 'Completed');
    const progress = updatedTasks.length > 0 
      ? Math.round((updatedTasks.filter(t => t.status === 'Completed').length / updatedTasks.length) * 100)
      : 0;

    updateCAPA(id, {
      tasks: updatedTasks,
      progress,
      status: allCompleted && capa.status === 'In Progress' ? 'Implemented' : capa.status
    });
  }, [capas, updateCAPA]);

  const submitCAPAForEffectiveness = useCallback((id) => {
    updateCAPA(id, {
      status: 'Effectiveness Check'
    });
  }, [updateCAPA]);

  const verifyEffectiveness = useCallback((id, result) => {
    const capa = capas.find(c => c.id === id);
    if (!capa) return;

    if (result === 'Pass') {
      updateCAPA(id, {
        status: 'Closed',
        effectivenessResult: 'Pass',
        effectivenessVerifiedBy: currentRole?.name || 'QA Head',
        effectivenessVerifiedAt: new Date().toISOString(),
        progress: 100
      });
    } else {
      updateCAPA(id, {
        status: 'In Progress',
        effectivenessResult: 'Fail',
        effectivenessVerifiedBy: currentRole?.name || 'QA Head',
        effectivenessVerifiedAt: new Date().toISOString()
      });
    }
  }, [capas, currentRole, updateCAPA]);

  const closeCAPA = useCallback((id) => {
    const capa = capas.find(c => c.id === id);
    if (!capa || capa.status !== 'Effectiveness Check' || capa.effectivenessResult !== 'Pass') {
      throw new Error('CAPA must pass effectiveness check before closing');
    }
    updateCAPA(id, {
      status: 'Closed',
      closedAt: new Date().toISOString()
    });
  }, [capas, updateCAPA]);

  const getCAPAById = useCallback((id) => {
    return capas.find(c => c.id === id);
  }, [capas]);

  const getCAPAsByDeviationId = useCallback((deviationId) => {
    return capas.filter(c => c.deviationId === deviationId);
  }, [capas]);

  // ========== CHANGE CONTROL ==========
  const createChangeControl = useCallback((payload) => {
    const areaCode = payload.area || area;
    const newCC = {
      id: generateCCId(areaCode),
      title: payload.title,
      type: payload.type,
      status: 'Draft',
      riskLevel: payload.riskLevel || 'Medium',
      initiator: payload.initiator || currentRole?.name || 'System',
      initiatedAt: payload.initiatedAt || new Date().toISOString(),
      targetDate: payload.targetDate || new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      reason: payload.reason || '',
      impactAssessment: payload.impactAssessment || {},
      approvers: payload.approvers || [],
      impactedSOPs: payload.impactedSOPs || [],
      impactedTraining: payload.impactedTraining || [],
      attachments: payload.attachments || [],
      capaId: payload.capaId || null,
      linkedReqs: payload.linkedReqs || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: currentRole?.name || 'System'
    };
    setChangeControls(prev => [...prev, newCC]);
    return newCC;
  }, [generateCCId, area, currentRole]);

  const updateChangeControl = useCallback((id, patch) => {
    setChangeControls(prev => prev.map(cc => 
      cc.id === id ? { ...cc, ...patch, updatedAt: new Date().toISOString() } : cc
    ));
  }, []);

  const submitChangeControl = useCallback((id) => {
    updateChangeControl(id, {
      status: 'Under Review'
    });
  }, [updateChangeControl]);

  const approveChangeControl = useCallback((id) => {
    updateChangeControl(id, {
      status: 'Approved',
      approvedBy: currentRole?.name || 'QA Head',
      approvedAt: new Date().toISOString()
    });
  }, [currentRole, updateChangeControl]);

  const rejectChangeControl = useCallback((id, reason) => {
    updateChangeControl(id, {
      status: 'Rejected',
      rejectionReason: reason,
      rejectedBy: currentRole?.name || 'QA Head',
      rejectedAt: new Date().toISOString()
    });
  }, [currentRole, updateChangeControl]);

  const implementChangeControl = useCallback((id) => {
    updateChangeControl(id, {
      status: 'Implemented',
      implementedAt: new Date().toISOString()
    });
  }, [updateChangeControl]);

  const closeChangeControl = useCallback((id, evidence) => {
    updateChangeControl(id, {
      status: 'Closed',
      closedAt: new Date().toISOString(),
      implementationEvidence: evidence
    });
  }, [updateChangeControl]);

  const linkChangeControlToCAPA = useCallback((changeControlId, capaId) => {
    updateChangeControl(changeControlId, {
      capaId
    });
  }, [updateChangeControl]);

  const getChangeControlById = useCallback((id) => {
    return changeControls.find(cc => cc.id === id);
  }, [changeControls]);

  // ========== DERIVED HELPERS ==========
  const deviationCounts = useMemo(() => {
    return {
      open: deviations.filter(d => d.status === 'Open').length,
      underInvestigation: deviations.filter(d => d.status === 'Under Investigation').length,
      capaAssigned: deviations.filter(d => d.status === 'CAPA Assigned').length,
      closed: deviations.filter(d => d.status === 'Closed').length
    };
  }, [deviations]);

  const capaCounts = useMemo(() => {
    return {
      total: capas.length,
      open: capas.filter(c => c.status !== 'Closed').length,
      aging30: capas.filter(c => {
        if (c.status === 'Closed') return false;
        const daysSince = Math.floor((new Date() - new Date(c.createdAt)) / (1000 * 60 * 60 * 24));
        return daysSince >= 30;
      }).length,
      effectiveness: capas.filter(c => c.status === 'Effectiveness Check').length
    };
  }, [capas]);

  const ccCounts = useMemo(() => {
    return {
      total: changeControls.length,
      pendingQA: changeControls.filter(cc => cc.status === 'Pending QA Approval' || cc.status === 'Under Review').length,
      implementation: changeControls.filter(cc => cc.status === 'Implementation' || cc.status === 'Implemented').length,
      highRisk: changeControls.filter(cc => cc.riskLevel === 'High').length
    };
  }, [changeControls]);

  // Filter helpers
  const filterDeviations = useCallback((filters) => {
    let filtered = [...deviations];
    
    // Only filter by plant if the deviation has a plant field AND it matches
    // (seed data might not have plant field, so don't filter them out)
    if (filters?.plant) {
      filtered = filtered.filter(d => !d.plant || d.plant === filters.plant);
    }
    if (filters?.area && filters.area !== 'all') {
      filtered = filtered.filter(d => d.area === filters.area);
    }
    if (filters?.status && filters.status !== 'all') {
      filtered = filtered.filter(d => d.status === filters.status);
    }
    if (filters?.severity && filters.severity !== 'all') {
      filtered = filtered.filter(d => d.severity === filters.severity);
    }
    if (filters?.search) {
      const query = filters.search.toLowerCase();
      filtered = filtered.filter(d => 
        d.title?.toLowerCase().includes(query) ||
        d.id?.toLowerCase().includes(query) ||
        d.description?.toLowerCase().includes(query)
      );
    }
    if (filters?.dateRange) {
      // Filter by reportedAt date
      const now = new Date();
      const daysAgo = filters.dateRange === '7days' ? 7 : filters.dateRange === '30days' ? 30 : 90;
      const cutoff = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(d => {
        if (!d.reportedAt) return true; // Include if no date
        return new Date(d.reportedAt) >= cutoff;
      });
    }

    return filtered;
  }, [deviations]);

  const filterCAPAs = useCallback((filters) => {
    let filtered = [...capas];
    
    if (filters?.status && filters.status !== 'all') {
      filtered = filtered.filter(c => c.status === filters.status);
    }
    if (filters?.search) {
      const query = filters.search.toLowerCase();
      filtered = filtered.filter(c => 
        c.title?.toLowerCase().includes(query) ||
        c.id?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [capas]);

  const filterChangeControls = useCallback((filters) => {
    let filtered = [...changeControls];
    
    if (filters?.status && filters.status !== 'all') {
      filtered = filtered.filter(cc => cc.status === filters.status);
    }
    if (filters?.search) {
      const query = filters.search.toLowerCase();
      filtered = filtered.filter(cc => 
        cc.title?.toLowerCase().includes(query) ||
        cc.id?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [changeControls]);

  const value = useMemo(() => ({
    // Data
    deviations,
    capas,
    changeControls,
    
    // Permissions
    permissions,
    can: (action) => {
      const [entity, operation] = action.split(':');
      return permissions[entity]?.[operation] || false;
    },
    
    // Deviations
    createDeviation,
    updateDeviation,
    addDeviationAttachment,
    startInvestigation,
    saveInvestigation,
    assignCAPAFromDeviation,
    closeDeviation,
    getDeviationById,
    deviationCounts,
    filterDeviations,
    
    // CAPA
    createCAPA,
    updateCAPA,
    addCAPATask,
    completeCAPATask,
    submitCAPAForEffectiveness,
    verifyEffectiveness,
    closeCAPA,
    getCAPAById,
    getCAPAsByDeviationId,
    capaCounts,
    filterCAPAs,
    
    // Change Control
    createChangeControl,
    updateChangeControl,
    submitChangeControl,
    approveChangeControl,
    rejectChangeControl,
    implementChangeControl,
    closeChangeControl,
    linkChangeControlToCAPA,
    getChangeControlById,
    ccCounts,
    filterChangeControls
  }), [
    deviations, capas, changeControls,
    permissions,
    createDeviation, updateDeviation, addDeviationAttachment, startInvestigation, saveInvestigation,
    assignCAPAFromDeviation, closeDeviation, getDeviationById, deviationCounts, filterDeviations,
    createCAPA, updateCAPA, addCAPATask, completeCAPATask, submitCAPAForEffectiveness,
    verifyEffectiveness, closeCAPA, getCAPAById, getCAPAsByDeviationId, capaCounts, filterCAPAs,
    createChangeControl, updateChangeControl, submitChangeControl, approveChangeControl,
    rejectChangeControl, implementChangeControl, closeChangeControl, linkChangeControlToCAPA,
    getChangeControlById, ccCounts, filterChangeControls
  ]);

  return (
    <QualityEventsContext.Provider value={value}>
      {children}
    </QualityEventsContext.Provider>
  );
};

export const useQualityEvents = () => {
  const context = useContext(QualityEventsContext);
  if (!context) {
    throw new Error('useQualityEvents must be used within QualityEventsProvider');
  }
  return context;
};

export default QualityEventsContext;

